import { useCallback, useEffect, useRef, useState } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import { api, type DailyLog, type DailyLogPatch } from './api';

export type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

// Taps land far faster than a mobile round-trip, so edits are coalesced for
// DEBOUNCE_MS. MAX_WAIT_MS caps how long a steady tapper can hold the queue
// open: without it the debounce restarts on every tap and nothing reaches the
// server until the tapping stops — and an iOS tab frozen mid-burst never fires
// the timer at all, taking the unsent edits with it.
const DEBOUNCE_MS = 350;
const MAX_WAIT_MS = 1200;
const SAVED_BADGE_MS = 1200;

// Stable autosave hook for a daily log. Returns a `patch` function that immediately
// applies optimistic state and queues a debounced PATCH.
export function usePatchDay(
  date: string,
  log: DailyLog | null,
  // Widened to the React state setter (Today.tsx already passes one) so every
  // write can go through a functional update. `patch` therefore never closes
  // over a `log` snapshot, and two edits in the same batch can't both compute
  // from the same stale day.
  setLog: Dispatch<SetStateAction<DailyLog | null>>,
) {
  const [status, setStatus] = useState<SaveStatus>('idle');
  const [error, setError] = useState<string | null>(null);

  // Fields edited locally that the server has not accepted yet. Cleared only
  // when a PATCH carrying them succeeds; a failed PATCH puts them back so the
  // next patch()/flush() retries them instead of dropping the edit silently.
  const pending = useRef<DailyLogPatch>({});
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const badgeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // When the oldest un-flushed edit was queued, for the MAX_WAIT_MS ceiling.
  const queuedSince = useRef(0);
  // True while a PATCH is open. Writes are strictly serialized — a flush that
  // arrives mid-request joins the queue instead of racing — so two responses
  // can never land out of order and resurrect a value the user moved past.
  const saving = useRef(false);
  // The body of the PATCH currently open. Those fields have left `pending` but
  // the server has not confirmed them yet, so commit() still has to defend them
  // against a response from another endpoint that predates them.
  const inFlightBody = useRef<DailyLogPatch>({});

  const flush = useCallback(async () => {
    if (saving.current) return;
    if (Object.keys(pending.current).length === 0) return;
    saving.current = true;
    try {
      // Drain loop: anything queued while a request was open is sent as soon as
      // that request answers, so a debounce timer that fired mid-flight (and
      // bailed on the guard above) never strands its edit.
      while (Object.keys(pending.current).length > 0) {
        const body = pending.current;
        pending.current = {};
        inFlightBody.current = body;
        queuedSince.current = 0;
        setStatus('saving');
        setError(null);
        try {
          const next = await api.patchDay(date, body);
          inFlightBody.current = {};
          // Anything the user changed while this request was open is newer than
          // the server's answer, so it goes back on top. Without this the number
          // visibly snaps back to a value the user already tapped past — and the
          // following tap, which counts up from what's on screen, just repeats
          // it, which is what makes the steppers look like they skip taps.
          const newer = pending.current as Partial<DailyLog>;
          setLog((prev) => (prev ? { ...prev, ...next, ...newer } : next));
          setStatus('saved');
          if (badgeTimer.current) clearTimeout(badgeTimer.current);
          badgeTimer.current = setTimeout(() => {
            badgeTimer.current = null;
            // Only retire our own badge — a save started since then owns the
            // indicator and must keep showing 'saving'/'error'.
            setStatus((s) => (s === 'saved' ? 'idle' : s));
          }, SAVED_BADGE_MS);
        } catch (e) {
          // Requeue the failed fields underneath anything queued since, so a
          // newer value still wins, and stop draining. The retry rides along
          // with the next patch() or an explicit flush(). Back in `pending`,
          // they are covered by the pending overlay again, so the in-flight
          // record is cleared rather than double-counted.
          pending.current = { ...body, ...pending.current };
          inFlightBody.current = {};
          setStatus('error');
          setError(String(e));
          return;
        }
      }
    } finally {
      saving.current = false;
    }
  }, [date, setLog]);

  const hasLog = log !== null;
  const patch = useCallback(
    (p: DailyLogPatch, opts?: { immediate?: boolean }) => {
      if (!hasLog) return;
      pending.current = { ...pending.current, ...p };
      // Optimistic, computed from whatever React holds right now rather than
      // from a captured render, so the UI feels instant without going stale.
      setLog((prev) => (prev ? { ...prev, ...(p as Partial<DailyLog>) } : prev));
      if (timer.current) {
        clearTimeout(timer.current);
        timer.current = null;
      }
      if (opts?.immediate) {
        queuedSince.current = 0;
        void flush();
        return;
      }
      const now = Date.now();
      if (queuedSince.current === 0) queuedSince.current = now;
      const wait = Math.max(0, Math.min(DEBOUNCE_MS, queuedSince.current + MAX_WAIT_MS - now));
      timer.current = setTimeout(() => {
        timer.current = null;
        void flush();
      }, wait);
    },
    [hasLog, setLog, flush],
  );

  // commit adopts a full day returned by an endpoint this hook does not own
  // (setWeight, setMeal, add/deleteOffPlanFood). Those responses are awaited at
  // the call site, so by the time they land the user may already have moved a
  // counter — and the response carries the server's older value for it. Merging
  // through here keeps the server's derived fields (macros, off-plan totals)
  // while re-applying every local edit the server hasn't confirmed yet, exactly
  // like the drain loop does for a PATCH response.
  //
  // Usage: `const day = await api.setMeal(id, true); commit(day);`
  const commit = useCallback(
    (serverLog: DailyLog) => {
      const unconfirmed = {
        ...inFlightBody.current,
        ...pending.current,
      } as Partial<DailyLog>;
      setLog((prev) => (prev ? { ...prev, ...serverLog, ...unconfirmed } : serverLog));
    },
    [setLog],
  );

  // iOS kills backgrounded PWA tabs without warning and never fires a reliable
  // beforeunload, so an edit still sitting in the debounce window dies with the
  // tab. Push it out the moment the page is hidden instead.
  //
  // The flush survives the suspension: request() in api.ts marks every small
  // JSON write `keepalive`, so the browser keeps the PATCH in flight even after
  // the document goes away — the same guarantee sendBeacon gives, without its
  // limitation (a beacon can only issue POST, and the route is registered
  // PATCH-only in handlers.go: "PATCH /api/v1/logs/daily/{date}", so it would
  // 405). What is still not covered is an edit the debounce is holding when the
  // tab dies without firing either event, and the fact that a request finishing
  // after the document is gone has nothing left to deliver its response to —
  // the write lands on the server, but this hook never sees the answer.
  useEffect(() => {
    const flushNow = () => {
      if (timer.current) {
        clearTimeout(timer.current);
        timer.current = null;
      }
      void flush();
    };
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') flushNow();
    };
    document.addEventListener('visibilitychange', onVisibility);
    // pagehide fires on the iOS back-forward-cache path where unload does not.
    window.addEventListener('pagehide', flushNow);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', flushNow);
    };
  }, [flush]);

  useEffect(() => {
    return () => {
      if (timer.current) {
        clearTimeout(timer.current);
        timer.current = null;
      }
      if (badgeTimer.current) {
        clearTimeout(badgeTimer.current);
        badgeTimer.current = null;
      }
      // Best-effort flush on unmount.
      void flush();
    };
  }, [flush]);

  return { patch, commit, status, error, flush };
}
